import { Box, StyleProps } from '@chakra-ui/react'
import {
  ContentNode,
  ContentNodeExtractorCache,
  ContentNodeUtils,
  FieldContentNode,
  RuntimeOperations,
} from '@helo/cms'
import { Assert, If, pipe } from '@helo/utils'
import {
  CellStyleModule,
  ClientSideRowModelModule,
  ColDef,
  ColGroupDef,
  ColumnApiModule,
  ColumnAutoSizeModule,
  ColumnMenuModule,
  ColumnPinnedEvent,
  ContextMenuModule,
  CustomFilterModule,
  DateFilterModule,
  GetContextMenuItems,
  GridOptions,
  GridStateModule,
  GroupFilterModule,
  LicenseManager,
  LocaleModule,
  ModuleRegistry,
  MultiFilterModule,
  NumberFilterModule,
  RenderApiModule,
  RowAutoHeightModule,
  RowSelectionModule,
  RowStyleModule,
  ServerSideRowModelApiModule,
  ServerSideRowModelModule,
  SetFilterModule,
  SortChangedEvent,
  TextFilterModule,
  ValidationModule,
  ValueGetterParams,
  themeMaterial,
} from 'ag-grid-enterprise'
import { AgGridReact, AgGridReactProps } from 'ag-grid-react'
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useDeepMemo } from '../../hooks/useOnChange'
import { useAppStore } from '../../store/appStore/appStore'
import { CellRenderer } from '../../widgets/TableWidget/cells/CellRenderer'
import { setupColumnDefs } from '../../widgets/TableWidget/setupColumnDefs'
import {
  CustomLoadingOverlay,
  NullRenderer,
} from '../LoadingOverlay/CustomLoadingOverlay'
import { RenderColumnMenu, TableFilterHeader } from './TableFilterHeader'

ModuleRegistry.registerModules([
  ServerSideRowModelModule,
  ValidationModule,
  LocaleModule,
  ContextMenuModule,
  ColumnAutoSizeModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  SetFilterModule,
  MultiFilterModule,
  GroupFilterModule,
  CustomFilterModule,
  RowStyleModule,
  RowAutoHeightModule,
  RowSelectionModule,
  CellStyleModule,
  ColumnApiModule,
  ServerSideRowModelApiModule,
  ColumnMenuModule,
  ClientSideRowModelModule,
  GridStateModule,
  RenderApiModule,
])

let hasSetAgGridLicense = false

export const ensureAgGridLicenseIsSet = () => {
  if (hasSetAgGridLicense) return

  const { AGGRID_LICENSE } = useAppStore.getState().config
  if (!AGGRID_LICENSE) {
    throw new Error('AGGRID_LICENSE is missing from app config state')
  }

  LicenseManager.setLicenseKey(AGGRID_LICENSE)
  hasSetAgGridLicense = true
}

export interface TableProps extends StyleProps {
  columnDefs?: (ColDef | ColGroupDef)[]
  getContextMenuItems?: GetContextMenuItems<unknown>
  gridOptions?: GridOptions
  onColumnMoved?: (params: any) => void
  onColumnResized?: (params: any) => void
  onColumnVisible?: (params: any) => void
  onColumnPinned?: (params: ColumnPinnedEvent) => void
  onFirstDataRendered?: (params: any) => void
  onGridReady?: (params: any) => void
  onSortChanged?: (params: SortChangedEvent<any>) => void
  serverSideDatasource?: any
  rowModelType?: any
  // Override the Table ref with a parent ag-grid-react ref.
  ref: any
  rowData: any[] | null
  defaultColDef?: ColDef
  context?: any
  getRowId?: AgGridReactProps['getRowId']
  loading?: boolean
  /**
   * Pass-through that supplies custom column-header menu content (e.g. the filter UI)
   * for each column. The Table does not hard-code any filter editor; the host
   * (e.g. TableWidget) provides the rendered content. Returning `null` renders no
   * menu affordance for that column.
   */
  renderColumnMenu?: RenderColumnMenu
  /**
   * Instantly autosizes a single column when its header autosize action is clicked.
   * When omitted, the header autosize control is not rendered.
   */
  onAutoSizeColumn?: (columnKey: string) => void
  /**
   * Bumps whenever column-menu content should refresh (e.g. filter state changed).
   * Included in AG Grid context so custom headers can key/remount menu content.
   */
  columnMenuRevision?: unknown
}

export const PAGE_SIZE = 150

export const Table = ({
  columnDefs,
  getContextMenuItems,
  gridOptions,
  onColumnMoved,
  onColumnResized,
  onColumnVisible,
  onColumnPinned,
  onGridReady,
  onSortChanged,
  rowData,
  borderRadius = '8px',
  serverSideDatasource,
  rowModelType,
  defaultColDef,
  context,
  getRowId,
  loading, // Add this prop
  onFirstDataRendered, // Add this prop
  renderColumnMenu,
  onAutoSizeColumn,
  columnMenuRevision,
  ref,
  ...props
}: TableProps) => {
  const gridRef = useRef<AgGridReact>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const contentNodeExtractorCacheRef = useRef<ContentNodeExtractorCache>({})
  const [sortRevision, setSortRevision] = useState(0)
  ensureAgGridLicenseIsSet()

  const handleSortChanged = (params: SortChangedEvent) => {
    setSortRevision(revision => revision + 1)
    onSortChanged?.(params)
  }

  const colDefs = useDeepMemo(() => setupColumnDefs(columnDefs), [columnDefs])

  const defaultColDefMemoized = useMemo((): ColDef => {
    return {
      editable: false,
      sortable: true,
      resizable: true,
      filter: false, // Temporarily disabled
      enableValue: true,
      lockPinned: false,
      // Enable third click to clear sort (asc -> desc -> none)
      sortingOrder: ['asc', 'desc', null],
      // has an effect on async vs sync autosizing function..
      cellDataType: false,
      cellRenderer: CellRenderer,
      valueGetter: getColumnFieldContentNode,

      // Required to mix fit grid with autosize by content.
      // Oherwise will fit many columns into grid width.
      minWidth: 135,
      // required to autosize before grid gets content, or if no content.
      // When mixed with minWidth, allows overflowing right.
      initialFlex: 1,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      /**
       * Without this being false, the columns close their
       * width when drug around, forcing an word wrap where there
       * wasn't before..
       */
      wrapText: false,
      /**
       * must be true for dynamic heights to work,
       * but at a major performance cost. Turn back on for test.
       *
       */
      autoHeight: false,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        // Required to prevent collapse on move columns.
        // maxWidth: '100%',
      },
      // Custom header replaces AG Grid's default header (and its legacy hamburger
      // column menu). Sorting is handled by the custom header; the filter UI is
      // supplied by the host via `renderColumnMenu`.
      headerComponent: TableFilterHeader,
      suppressHeaderMenuButton: true,
      ...defaultColDef,
    }
  }, [defaultColDef])

  // Merge host pass-throughs into AG Grid's context so the custom header can
  // render filter content and autosize without the Table importing editors.
  //
  // The host's `renderColumnMenu` / `onAutoSizeColumn` may get a new identity
  // from unrelated state churn (for example query content-node refreshes during
  // row loading). Keep stable wrappers in context and refresh headers only when
  // the host-provided revision says the menu's rendered state meaningfully changed.
  const renderColumnMenuRef = useRef(renderColumnMenu)
  renderColumnMenuRef.current = renderColumnMenu

  const onAutoSizeColumnRef = useRef(onAutoSizeColumn)
  onAutoSizeColumnRef.current = onAutoSizeColumn

  const stableRenderColumnMenu = useCallback<RenderColumnMenu>(
    params => renderColumnMenuRef.current?.(params) ?? null,
    [],
  )

  const stableOnAutoSizeColumn = useCallback((columnKey: string) => {
    onAutoSizeColumnRef.current?.(columnKey)
  }, [])

  const mergedContext = useMemo(
    () => ({
      ...(context ?? {}),
      renderColumnMenu: stableRenderColumnMenu,
      onAutoSizeColumn: onAutoSizeColumn ? stableOnAutoSizeColumn : undefined,
      columnMenuRevision,
      sortRevision,
      contentNodeExtractorCache: contentNodeExtractorCacheRef.current,
    }),
    [
      context,
      stableRenderColumnMenu,
      onAutoSizeColumn,
      stableOnAutoSizeColumn,
      columnMenuRevision,
      sortRevision,
    ],
  )

  useEffect(() => {
    // Re-render custom headers when column-menu or sort state changes.
    // AG Grid does not re-render header components when `context` alone
    // changes, so we must call refreshHeader after sortRevision bumps.
    // Requires RenderApiModule for api.refreshHeader().
    gridRef.current?.api?.refreshHeader()
  }, [columnMenuRevision, sortRevision])

  useImperativeHandle(ref, () => gridRef.current as AgGridReact, [])

  const sizeColumns = () => {
    const api = gridRef.current?.api
    if (!api) return

    api.autoSizeAllColumns()
    // above is async, so need to wait.
    setTimeout(() => {
      if (!containerRef.current || !gridRef.current?.api) return
      const rowViewport =
        containerRef.current.querySelector('.ag-body-viewport')
      if (!rowViewport) return
      const rowViewportWidth = rowViewport.clientWidth
      const centerCols = rowViewport.querySelector('.ag-center-cols-container')
      const pinnedLeft = rowViewport.querySelector(
        '.ag-pinned-left-cols-container',
      )
      const pinnedRight = rowViewport.querySelector(
        '.ag-pinned-right-cols-container',
      )
      if (!centerCols || !pinnedLeft || !pinnedRight) return
      const rowViewPortChildrenWidth =
        centerCols.clientWidth +
        pinnedLeft.clientWidth +
        pinnedRight.clientWidth

      if (rowViewportWidth < rowViewPortChildrenWidth) {
        gridRef.current.api.autoSizeAllColumns()
      } else {
        gridRef.current.api.sizeColumnsToFit({})
      }
    }, 0)
  }

  useEffect(() => {
    // Required to have autosize work in tabs that aren't visisble yet.
    const currentRef = containerRef.current

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            sizeColumns()
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.1 },
    )

    if (currentRef) observer.observe(currentRef)

    return () => {
      if (currentRef) observer.unobserve(currentRef)
    }
  }, [])

  return (
    <Box
      className=" ag-grid-container"
      width="full"
      minHeight="335px"
      {...props}
      ref={containerRef}
    >
      {/** size is controlled by parent. */}
      <AgGridReact
        ref={gridRef}
        processCellForClipboard={params =>
          fieldToText(
            params.value,
            params.column?.getColDef().context?.tableColumnConfig,
          )
        }
        onGridReady={onGridReady}
        localeText={{ startsWith: 'Starts with' }} // defaults to begins with.
        // must use loading to show full page loader...
        loading={loading}
        theme={themeMaterial}
        loadThemeGoogleFonts={true}
        rowData={rowData}
        getRowId={getRowId}
        onColumnPinned={onColumnPinned}
        getContextMenuItems={getContextMenuItems as any}
        onColumnVisible={onColumnVisible}
        onColumnMoved={onColumnMoved}
        onColumnResized={onColumnResized}
        columnDefs={colDefs as (ColDef | ColGroupDef)[]}
        rowModelType={rowModelType ?? 'clientSide'}
        serverSideDatasource={serverSideDatasource}
        // sets the minimum height. If autoheight is on, will still expand.
        // this should be the minimum usual height for our rows.
        rowHeight={44}
        headerHeight={42}
        rowSelection={{
          mode: 'multiRow',
          checkboxes: false,
          headerCheckbox: false,
          enableClickSelection: true,
        }}
        // /**
        //  * Works the same as suppressing row virtualization.
        //  * How many rows above/below should render. This has
        //  * a large effect on loading performance.
        //  *
        //  * Setting to 10 (sould be default) for a faster initial render.
        //  * Down below, after first data rendered, setting to 50 for
        //  * a smoother scroll experience.
        //  */
        rowBuffer={10}
        onSortChanged={handleSortChanged}
        // Same as page size. Literally sets page size.
        cacheBlockSize={PAGE_SIZE}
        autoSizeStrategy={{ type: 'fitCellContents' }}
        gridOptions={gridOptions}
        context={mergedContext}
        defaultColDef={defaultColDefMemoized}
        // Initially autosize first before doing math for how they should autosize.
        onFirstDataRendered={params => {
          sizeColumns()
          onFirstDataRendered?.(params)
        }}
        suppressRowVirtualisation={false}
        suppressColumnVirtualisation={true}
        rowStyle={loading ? { opacity: 0 } : {}}
        columnMenu="legacy" // prevents filter from showing up always.
        loadingOverlayComponent={CustomLoadingOverlay}
        // prevents showing the single loading row intiially
        loadingCellRenderer={loading ? NullRenderer : undefined}
      />
    </Box>
  )
}

export const getColumnFieldContentNode = (
  params: ValueGetterParams,
): FieldContentNode => {
  const row = params.data as ContentNode | undefined
  const columnConfig = params.colDef.context?.tableColumnConfig
  const extractorCache = params.context?.contentNodeExtractorCache as
    | ContentNodeExtractorCache
    | undefined

  const { key: _columnConfigKey, ...columnConfigRest } =
    columnConfig.columnConfig ?? {}

  return pipe(
    [row as ContentNode],
    ContentNodeUtils.findContentNode(columnConfig.selector, extractorCache),
    Assert.orThrow(
      ContentNodeUtils.isKind('FIELD'),
      () => new Error('Content node not found for column config'),
    ),
    If.then(
      () => Object.keys(columnConfigRest).length,
      ContentNodeUtils.setState([columnConfigRest] as any),
    ),
    ContentNodeUtils.deeplySetState({ size: 'small' }),
  )
}

export const fieldToText = (
  fieldContentNode: FieldContentNode,
  columnConfig?: any,
): string => {
  if (!fieldContentNode) {
    return '-'
  }

  // Column overrides only — never DataTypeConfig `config.state`, which would
  // clobber already-processed field asText (e.g. parent REFERENCE chains).
  const columnState = columnConfig?.state ?? columnConfig?.columnConfig?.state

  const node =
    columnState && Object.keys(columnState).length > 0
      ? pipe(
          fieldContentNode,
          ContentNodeUtils.setState([{ state: columnState }] as any),
          RuntimeOperations.processContentNode(),
        )
      : fieldContentNode

  // ISO dates and ungrouped numbers via call-site override; default emptyFormat keeps clipboard '-'.
  return pipe(
    node,
    ContentNodeUtils.asText({ dateFormat: 'iso', numberFormat: 'raw' }),
  )
}
